import React, { useContext as useContextCore, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import omit from 'lodash/omit'
import type { DocumentNode } from '@apollo/client'
import type { ListFieldConfig, UseDocumentResult, UseListResult } from '@ztechapps/common.hook'
import type { AiCreditTransaction, AiCreditTransactionTotals } from '@ztechapps/common.contract'
import type { CreditAmountFormat } from '@ztechapps/ai.common'
import type { SelectOption } from '@ztechapps/webui.input'
import { useDocument, useList } from '@ztechapps/common.hook'
import { AiBillingScopeEnum } from '@ztechapps/common.contract'
import { AI_CREDIT_TRANSACTION_LIST_GET_TOTALS_QUERY } from '../graphql'
import { USAGE_USER_FIELD, usageListFieldConfig } from './constants'

// The user picked in the per-user filter — the whole `allUser` option, so the select can show it.
export type UsageUserOption = SelectOption<{ _id: string }>

type UsageListContextType = {
  ownerId?: string
  // Tenant scope: one balance shared by every user, so the table adds the user column and filter.
  byUser: boolean
  fields: ListFieldConfig
  filterCategory: string
  usageList: UseListResult<AiCreditTransaction>
  usageTotals: UseDocumentResult<AiCreditTransactionTotals>
  userFilter: UsageUserOption | null
  setUserFilter: (user: UsageUserOption | null) => void
  onRefresh: () => Promise<void>
  // The account currency code and its display format (symbol/decimals), resolved once by the
  // caller and handed down so every consumer renders the same rate as the balance it shows.
  currency?: string
  currencyFormat?: CreditAmountFormat
}

const context = React.createContext<UsageListContextType>({} as UsageListContextType)

export type UsageListProviderProps = {
  children: React.ReactNode
  // The list query: `aiCreditTransactionList` plus its sibling `aiCreditTransactionListMeta`.
  query: DocumentNode
  ownerId?: string
  billingScope: AiBillingScopeEnum
  filterCategory: string
  currency?: string
  currencyFormat?: CreditAmountFormat
  // Operation names the caller's own balance read runs under, refetched with the table so a refresh
  // cannot show new rows while the balance stays stale.
  refetchQueries: string[]
}

export const UsageListProvider = ({
  children,
  query,
  ownerId,
  billingScope,
  filterCategory,
  currency,
  currencyFormat,
  refetchQueries,
}: UsageListProviderProps) => {
  const client = useApolloClient()
  const byUser = billingScope === AiBillingScopeEnum.Tenant
  // The picked option, kept so the select can show it; the narrowing itself lives in the list's table filter.
  const [pickedUser, setPickedUser] = useState<UsageUserOption | null>(null)

  // No persistence — one filter category serves every owner, so a stored filter would carry one
  // owner's filter into another's table (a stored ownerId wins over the fresh one on merge);
  // mirrors apps/panelpunches/admin/src/components/accounting-statement/CompanyAccountingStatement.tsx:25-27.
  const usageList = useList<AiCreditTransaction>({
    query,
    entity: 'aiCreditTransactionList',
    sort: { createdAt: -1 },
    limit: 100,
    filter: { ownerId },
    options: { queryOptions: { skip: !ownerId, fetchPolicy: 'cache-and-network' } },
  })
  const usageTotals = useDocument<AiCreditTransactionTotals>({
    query: AI_CREDIT_TRANSACTION_LIST_GET_TOTALS_QUERY,
    entity: 'aiCreditTransactionListGetTotals',
    filter: { ...usageList.filter, ownerId },
    options: { queryOptions: { skip: !ownerId } },
  })

  // Added through the list's own filter step so it composes beside the search, template and column
  // filters; a change to the base `filter` above would reset them (useListAction re-syncs to it).
  const setUserFilter = (user: UsageUserOption | null) => {
    setPickedUser(user)
    usageList.onFilter({ tableFilter: { createdBy: user ? { $eq: user._id } : undefined } })
  }

  const onRefresh = async () => {
    await usageList.refetch()
    await usageTotals.refetch()
    await client.refetchQueries({ include: refetchQueries })
  }

  return (
    <context.Provider
      value={{
        ownerId,
        byUser,
        fields: byUser ? usageListFieldConfig : omit(usageListFieldConfig, USAGE_USER_FIELD),
        filterCategory,
        usageList,
        usageTotals,
        // "Reset filters" clears the table filter, so the select follows it back to empty.
        userFilter: usageList.tableFilter?.createdBy ? pickedUser : null,
        setUserFilter,
        onRefresh,
        currency,
        currencyFormat,
      }}
    >
      {children}
    </context.Provider>
  )
}

export const useUsageListContext = () => useContextCore(context)
